import ReactEcharts from 'echarts-for-react';
import { f7, useStore } from 'framework7-react';
import { FC, useMemo } from 'react';
import { RecommendedCarsSection } from './RecommendedCarsSection';
import style from './carAssetValueStyle.module.css';
import { useCarAssetValue } from './hook/useCarAssetValue';
import { useRecommendedCars } from './hook/useRecommendedCars';
import carAssetValueBanner from '@/assets/images/carAssetValueBanner.png';
import carDefault from '@/assets/images/carImg.svg';
import { PageTemplate } from '@/components/uiParts/Template/PageTemplate/PageTemplate';
import { paths } from '@/config/paths';
import { store } from '@/config/store';
import { Car } from '@/types/api/customerApi';
import { getCarPhoto } from '@/utils/utils';

const formatManYen = (price: number): string => (price / 10000).toFixed(1);

interface CarAssetValuePageProps {
  overrideMlg?: number;
  overrideExColId?: string;
}

export const CarAssetValuePage: FC<CarAssetValuePageProps> = ({
  overrideMlg,
  overrideExColId,
}) => {
  const carItemInfo = useStore(store, 'carItemInfo') as Required<Car>;
  const { data, isLoading, errorMessage } = useCarAssetValue({
    tStockCarId: carItemInfo?.t_stock_car_id,
    mlg: overrideMlg,
    exColId: overrideExColId,
  });
  const {
    data: recommendedCarsData,
    isLoading: isRecommendedCarsLoading,
    errorMessage: recommendedCarsErrorMessage,
  } = useRecommendedCars({
    tStockCarId: carItemInfo?.t_stock_car_id,
  });

  const carName = [carItemInfo?.maker_nm, carItemInfo?.car_nm]
    .filter(Boolean)
    .join(' ');

  const carImageUrl = getCarPhoto(carItemInfo, false);

  const licensePlate =
    carItemInfo?.transport_name &&
    carItemInfo?.class_no &&
    carItemInfo?.hiragana &&
    carItemInfo?.registry_no
      ? `${carItemInfo.transport_name} ${carItemInfo.class_no}　${carItemInfo.hiragana} ${carItemInfo.registry_no}`
      : null;

  const now = new Date();
  const currentDateLabel = `${now.getFullYear()}/${String(
    now.getMonth() + 1,
  ).padStart(2, '0')}`;

  const firstPrice = data?.future_prices?.[0];
  const residualRateLabel =
    firstPrice?.residual_rate != null
      ? (firstPrice.residual_rate * 100).toFixed(2)
      : null;

  const priceBadgeLabel =
    data?.simple_ll != null && data?.simple_ul != null
      ? `${formatManYen(data.simple_ll)}〜${formatManYen(data.simple_ul)}万円`
      : null;

  // グラフ用データ（車両価格 × 経過月）
  const chartOption = useMemo(() => {
    if (!data?.future_prices?.length) return null;

    const futurePrices = data.future_prices;

    // 基準日を計算（今日 − 最初の経過月数）
    const firstElapsedMonth = futurePrices[0].elapsed_month;
    const baseDate = new Date();
    baseDate.setMonth(baseDate.getMonth() - firstElapsedMonth);
    const baseDateLabel = `${baseDate.getFullYear()}/${String(
      baseDate.getMonth() + 1,
    ).padStart(2, '0')}`;

    // 60ヶ月（5年）以内に絞り、[elapsed_month, 万円] 形式に変換
    const MAX_MONTH = 60;
    const seriesData = futurePrices
      .filter((p) => p.future_price > 0 && p.elapsed_month <= MAX_MONTH)
      .map((p) => [p.elapsed_month, Math.round(p.future_price / 1000) / 10]);

    const priceValues = seriesData.map((d) => d[1]);
    const minY = Math.floor((Math.min(...priceValues) * 0.9) / 10) * 10;
    const maxY = Math.ceil((Math.max(...priceValues) * 1.1) / 10) * 10;

    return {
      legend: { show: false },
      tooltip: {
        trigger: 'axis',
        formatter: (params: { value: [number, number] }[]) => {
          const val = params[0]?.value?.[1];
          return val != null ? `${val}万円` : '';
        },
      },
      grid: { left: '14%', right: '5%', top: '5%', bottom: '25%' },
      xAxis: {
        type: 'value',
        min: 0,
        max: MAX_MONTH,
        interval: 12,
        axisLabel: {
          fontSize: 10,
          formatter: (value: number) => {
            if (value === 0) return baseDateLabel;
            if (value % 12 === 0) return `${value / 12}年後`;
            return '';
          },
        },
      },
      yAxis: {
        type: 'value',
        axisLabel: { formatter: '{value}万円', fontSize: 10 },
        min: minY,
        max: maxY,
      },
      series: [
        {
          type: 'line',
          data: seriesData,
          smooth: true,
          lineStyle: { color: '#1E55E6', width: 2 },
          symbol: 'none',
        },
      ],
    };
  }, [data]);

  return (
    <PageTemplate pageName="car-asset-value" title="愛車の資産価値" showNavBack>
      <div className={style['container']}>
        <div className={style['main-card']}>
          {/* 車両情報 */}
          <div className={style['car-info-section']}>
            <div className={style['car-image-wrapper']}>
              <img
                src={carImageUrl || carDefault}
                onError={({ currentTarget }) =>
                  (currentTarget.src = carDefault)
                }
                className={style['car-image']}
                alt={carName}
              />
            </div>
            {carName && <p className={style['car-name']}>{carName}</p>}
            {licensePlate && (
              <p className={style['plate-number']}>{licensePlate}</p>
            )}
          </div>

          {/* ローディング */}
          {isLoading && (
            <div className={style['loading']}>
              <span>資産価値を算出中...</span>
            </div>
          )}

          {/* エラー */}
          {!isLoading && errorMessage && (
            <div className={style['error-message']}>
              <p>{errorMessage}</p>
            </div>
          )}

          {/* データ表示 */}
          {!isLoading && data && (
            <>
              {/* グラフ */}
              {chartOption && (
                <>
                  <div className={style['divider']} />
                  <p className={style['chart-title']}>
                    車両の資産価値推移・残価率（予測）
                  </p>
                  {(priceBadgeLabel || residualRateLabel) && (
                    <div className={style['asset-badge']}>
                      <div className={style['badge-date-bar']}>
                        {currentDateLabel}時点
                      </div>
                      <p className={style['badge-value']}>
                        {priceBadgeLabel && <span>{priceBadgeLabel}</span>}
                        {residualRateLabel && (
                          <span>
                            残価率 <strong>{residualRateLabel}</strong>
                            <small>%</small>
                          </span>
                        )}
                      </p>
                    </div>
                  )}
                  <ReactEcharts
                    option={chartOption}
                    style={{ height: '220px', width: '100%' }}
                    notMerge
                  />
                  {data.lgc_ptn && (
                    <p className={style['logic-pattern']}>
                      算出根拠: {data.lgc_ptn}
                    </p>
                  )}
                </>
              )}

              {/* 車両情報サマリー */}
              <div className={style['divider']} />
              <div className={style['info-section']}>
                <div className={style['info-row']}>
                  <span className={style['info-label']}>年式</span>
                  <span className={style['info-value']}>
                    {carItemInfo?.registration_date
                      ? `${new Date(
                          carItemInfo.registration_date,
                        ).getFullYear()}年`
                      : data.mdl_y > 0
                      ? `${data.mdl_y}年`
                      : '---'}
                  </span>
                </div>
                <div className={style['info-row']}>
                  <span className={style['info-label']}>走行距離</span>
                  <span className={style['info-value']}>
                    {data.mlg > 0 ? `${data.mlg.toLocaleString()}km` : '---'}
                  </span>
                </div>
                <div className={style['info-row']}>
                  <span className={style['info-label']}>グレード</span>
                  <span className={style['info-value']}>
                    {data.model || '---'}
                  </span>
                </div>
                <div className={style['info-row']}>
                  <span className={style['info-label']}>評価点</span>
                  <span className={style['info-value']}>
                    {data.evl || '---'}
                  </span>
                </div>
              </div>
            </>
          )}

          {!isLoading && data && (
            <>
              <div className={style['divider']} />
              <RecommendedCarsSection
                cars={recommendedCarsData?.recommended_cars ?? []}
                isLoading={isRecommendedCarsLoading}
                errorMessage={recommendedCarsErrorMessage}
              />
            </>
          )}

          {/* バナー・CTAセクション */}
          {!isLoading && data && (
            <>
              <div className={style['divider']} />
              <img
                src={carAssetValueBanner}
                className={style['cta-banner']}
                alt="乗り方で価値が変わる"
                onClick={() =>
                  f7.view.main.router.navigate(paths.carAssetValueTips)
                }
              />
              <button
                type="button"
                className={style['cta-button']}
                onClick={() =>
                  f7.view.main.router.navigate(paths.carAssetValueInput)
                }
              >
                より詳しく資産価値を査定する
              </button>
            </>
          )}
        </div>
      </div>
    </PageTemplate>
  );
};
